import { useState, useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import useSWR from 'swr';
import { GridColDef } from '@mui/x-data-grid-pro';
import { dataGridCol } from '@shared/components/grid/common';
import { formatString, getOptions } from '@shared/commonFunctions';
import { statesList } from '@shared/constants/statesList';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { SERVICES } from '@shared/constants/apiConfig';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { TableModal } from '@shared/components/modals/TableModal';
import { useUsers } from '@shared/hooks/useUsers';
import { ILenderListItem } from '@modules/admin/Tools/shared/LenderControlsHeader';
import { Wrapper } from '@styles/CommonStyles';
import { IStateLenderRest, IStateLenderDropdownUser, IUser } from './interfaces';
import { requestCreateStateUser, requestDeleteStateUser, requestUpdateStateUser } from './queries';

const stateUsersSchema = Yup.object().shape(
  {
    state: Yup.string().when(['lenderId'], {
      is: (lenderId: string) => !!lenderId,
      then: Yup.string().notRequired(),
      otherwise: Yup.string().required(),
    }),
    lenderId: Yup.string().when(['state'], {
      is: (state: string) => !!state,
      then: Yup.string().notRequired(),
      otherwise: Yup.string().required(),
    }),
    users: Yup.array().of(Yup.string().required()).min(1, 'User are required'),
  },
  [['state', 'lenderId']]
);

const StateLenderRestrictions = () => {
  const { data: stateUsersList, error: stateUsersListError } = useSWR<IStateLenderRest[]>(`${SERVICES.USER}/statelenderuserrestriction`);
  const { data: lenders } = useSWR<ILenderListItem[]>(`/${SERVICES.LENDER}/lenders`, {
    fallbackData: [] as ILenderListItem[],
    revalidateOnMount: true,
  });

  const { users } = useUsers();
  const [currentData, setCurrentData] = useState<IStateLenderDropdownUser>();
  const [openModal, setOpenModal] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  const handleAddNewRecordBtn = useCallback(async(data: IStateLenderDropdownUser) => {
    setSubmitting(true);
	const formattedData = {...data, lenderId: data?.lenderId ? data?.lenderId : null};
	
    if (data.id) {
      await requestUpdateStateUser(formattedData);
    } else {
      await requestCreateStateUser(formattedData);
    }

    setSubmitting(false);
    setOpenModal(false);
  }, []);

  const handleNewBtn = useCallback(() => {
    setOpenModal(true);
    setCurrentData({} as IStateLenderDropdownUser);
  }, []);

  const gridActions = useMemo(() => ({
    onEdit: (data: IStateLenderRest) => {
      const mapUsers = data.usersDetails?.map((user) => user.userId);
      const editFormData = {
        id: data?.id,
        state: data?.state,
        users: mapUsers || [],
        lenderId: data?.lenderId,
      };
      setCurrentData(editFormData);
      setOpenModal(true);
    },
    onDelete: (data: IStateLenderRest) => requestDeleteStateUser(data?.id),
  }), []);

  const stateUsersFormConfig = useMemo(
    () => [
      {
        width: '100%',
        name: 'state',
        label: 'State',
        options: statesList,
        component: SelectAutocompleteField,
      },
      {
        width: '100%',
        name: 'lenderId',
        label: 'Lender',
        options: getOptions(lenders || []),
        component: SelectAutocompleteField,
      },
      {
        width: '100%',
        name: 'users',
        label: 'Users',
        options: getOptions(users?.groupUsers || [], 'userId', 'userName'),
        multiple: true,
        component: SelectAutocompleteField,
      },
    ],
    [users, lenders]
  );

  const stateUsersColumns: GridColDef[] = useMemo(
    () => [
      dataGridCol('state', {
        minWidth: 150,
        flex: 1.5,
        headerName: 'State',
      }),
      dataGridCol('lenderId', {
        headerName: 'Lender',
        minWidth: 250,
        flex: 2.5,
        valueFormatter: ({ value }) => formatString(lenders?.find((lender) => lender.id === value)?.name || ''),
      }),
      dataGridCol('usersDetails', {
        headerName: 'Users',
        minWidth: 600,
        flex: 6,
        valueFormatter: ({ value }) => value?.map((user: IUser) => formatString(user.userName))?.join(', '),
      }),
    ],
    [lenders]
  );

  const editingData = useMemo<IStateLenderDropdownUser>(
    () => ({
      id: currentData?.id ?? '',
      state: currentData?.state ?? '',
      lenderId: currentData?.lenderId ?? '',
      users: currentData?.users ?? [],
    }),
    [currentData]
  );

  return (
    <Wrapper>
      <TableModal
        isOpen={!!openModal}
        editingData={editingData}
        formConfig={stateUsersFormConfig}
        validationSchema={stateUsersSchema}
        isSubmitting={isSubmitting}
        onSubmit={handleAddNewRecordBtn}
        onClose={() => setOpenModal(false)}
      />
      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        buttonTitle="Add New Record"
        name="State Lender Users Restriction"
        columns={stateUsersColumns}
        actions={gridActions}
        loading={!stateUsersList && !stateUsersListError}
        rows={stateUsersList || []}
        onButtonClick={handleNewBtn}
      />
    </Wrapper>
  );
};

export default StateLenderRestrictions;
